import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PACKAGE_LEVEL_V3, TYPE_POPUP_MIGRATE } from '@src/constants/package';
import { checkCustomPackage, getPackageNameV3 } from '@src/services/package';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import { StyledDialogUpDownGradeV3 } from './index.style';
import V3BuyPackageContent from './V3BuyPackageContent';

const PackageV3ChangeInfoPopup = ({
  typeCheckUpDownGradePackV3,
  isAutoRenewing,
}) => {
  const { usingPackage } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);
  const { package: packageState } = useSelector((state) => state.order);
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const devices = queryParams.get('devices');
  const voiceClonings = queryParams.get('voiceClonings');
  const maxCredits = queryParams.get('credits');

  const packageName = (pack) =>
    getPackageNameV3(pack?.name, pack?.code, language, t);

  const usingPackageInfo = useMemo(() => {
    const isUsingCustomPackage = checkCustomPackage({
      level: usingPackage.level,
      type: usingPackage.type,
    });

    if (isUsingCustomPackage)
      return {
        ...usingPackage,
        device: user.maxDevices,
        maxCloningVoices: user.maxCloningVoices,
      };

    return usingPackage;
  }, [usingPackage, user]);

  const packageStateInfo = useMemo(() => {
    const isUsingCustomPackage = checkCustomPackage({
      level: packageState.level,
      type: packageState.type,
    });

    if (isUsingCustomPackage)
      return {
        ...packageState,
        device: devices,
        maxCloningVoices: voiceClonings,
        maxCredits: Number(maxCredits),
      };

    return packageState;
  }, [packageState, devices, voiceClonings, maxCredits]);

  const levelText = (currPkg, buyPkg) => {
    const MAPPING_PACKAGE_LEVEL = {
      [PACKAGE_LEVEL_V3.BASIC]: 0,
      [PACKAGE_LEVEL_V3.STANDARD]: 1,
      [PACKAGE_LEVEL_V3.SPECIAL]: 2,
      [PACKAGE_LEVEL_V3.VIP]: 3,
      [PACKAGE_LEVEL_V3.CUSTOM]: 4,
    };
    const isPackageCustom = (level, type) =>
      checkCustomPackage({
        level,
        type,
      });
    const isDowngrade =
      MAPPING_PACKAGE_LEVEL[buyPkg.level] <
      MAPPING_PACKAGE_LEVEL[currPkg.level];
    if (
      isPackageCustom(buyPkg.level, buyPkg.type) ||
      isPackageCustom(currPkg.level, currPkg.type)
    ) {
      return t('to');
    }

    if (buyPkg?.level === currPkg?.level) {
      if (buyPkg?.expiresIn > currPkg?.expiresIn) return t('up');
      if (buyPkg?.expiresIn < currPkg?.expiresIn) return t('down');
      return '';
    }

    if (isDowngrade) return t('down');
    return t('up');
  };

  return (
    <StyledDialogUpDownGradeV3>
      <div className="content-wrapper">
        <div className="content">
          {[
            TYPE_POPUP_MIGRATE.TYPE_MIGRATE_V1_V2_TO_V3,
            TYPE_POPUP_MIGRATE.TYPE_V3_SAME_LEVEL,
          ].includes(typeCheckUpDownGradePackV3) ? (
            <Typography
              className="info-package-mirgate"
              component="span"
              dangerouslySetInnerHTML={{
                __html: t('buyPackageV3', {
                  packageName: packageName(packageState),
                }),
              }}
            />
          ) : (
            <Typography
              className="info-package-mirgate"
              component="span"
              dangerouslySetInnerHTML={{
                __html: t('changeFromPackageTitle', {
                  fromPackage: packageName(usingPackage),
                  toPackage: packageName(packageState),
                  level: levelText(usingPackage, packageState),
                }),
              }}
            />
          )}

          <Typography className="note-payment" component="div">
            {t('notePayment')}
          </Typography>

          <V3BuyPackageContent
            usingPackage={usingPackageInfo}
            packageState={packageStateInfo}
            isAutoRenewing={isAutoRenewing}
          />

          <div className="support-payment">
            <Typography
              component="span"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(t('supportPayment'), {
                  allowedTags: ['a'],
                  allowedAttributes: {
                    a: ['href', 'target', 'rel', 'data-id', 'style'],
                  },
                }),
              }}
            />
          </div>
        </div>
      </div>
    </StyledDialogUpDownGradeV3>
  );
};

export default PackageV3ChangeInfoPopup;
